/**
 * Connect tab — "Call this AI Service" section (task 380, Mockup M).
 *
 * Code consumers second: the public endpoint URL bar, the x-api-key auth note
 * with in-place key minting (POST /api-keys — the plaintext key is shown once,
 * inline, and never recoverable afterwards), cURL / JavaScript / Python call
 * snippets generated from the endpoint's stored input schema, and a response
 * example generated from the output schema.
 */
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { apiKeysApi, type MintedApiKey } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { customDomainUrl } from '@/utils/endpointUrl';
import type { Endpoint } from '@/types';
import {
  buildResponseExample,
  buildSnippets,
  serviceUrl,
  type SnippetLanguage,
} from './serviceSnippets';

interface ServiceCallSectionProps {
  endpoint: Endpoint;
}

const LANGUAGES: Array<{ id: SnippetLanguage; label: string }> = [
  { id: 'curl', label: 'cURL' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'python', label: 'Python' },
];

/** Dark code block with a copy button, shared by the snippet and response views. */
function CodeBlock({
  code,
  copyLabel,
  onCopy,
  testId,
}: {
  code: string;
  copyLabel: string;
  onCopy: (code: string, label: string) => void;
  testId?: string;
}) {
  return (
    <div className="relative mt-3">
      <button
        type="button"
        onClick={() => onCopy(code, copyLabel)}
        className="absolute top-2 right-2 rounded-md bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-200 hover:bg-gray-600"
      >
        Copy
      </button>
      <pre
        data-testid={testId}
        className="overflow-x-auto rounded-md bg-gray-900 dark:bg-gray-950 p-4 text-xs leading-relaxed text-gray-100 font-mono"
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function ServiceCallSection({ endpoint }: ServiceCallSectionProps) {
  const toast = useToast();
  // The shortcut above stays the fast path for a developer already on this tab;
  // reviewing or revoking a key is the settings page's job (task 974), so the
  // link only appears where that page exists.
  const apiKeysPageEnabled = useFlag('innovate.fe.settings-api-keys.page');
  const [language, setLanguage] = useState<SnippetLanguage>('curl');
  const [urlCopied, setUrlCopied] = useState(false);
  const [mintedKey, setMintedKey] = useState<MintedApiKey | null>(null);

  /**
   * THE ADDRESS THIS SECTION HANDS OVER (task 857).
   *
   * This is connection information — everything here is copied into someone
   * else's system — so an account serving on its own hostname leads with that
   * hostname, and the platform URL follows as the alternative it always is.
   * With no active domain the list is empty and this renders exactly as before.
   */
  const customDomains = useActiveCustomDomainHostnames();
  const primaryHostname = customDomains[0] ?? null;
  const platformUrl = useMemo(() => serviceUrl(endpoint), [endpoint]);
  const url = useMemo(
    () => (primaryHostname ? customDomainUrl(endpoint, primaryHostname) : platformUrl),
    [endpoint, primaryHostname, platformUrl]
  );
  /**
   * Every other address that also reaches this service: the platform URL, which
   * never stops working, then any further domains the account holds — a domain
   * aliases the whole surface, so each of them is equally correct.
   */
  const alternateUrls = useMemo(
    () =>
      primaryHostname
        ? [
            { label: 'Platform URL (always available)', value: platformUrl },
            ...customDomains.slice(1).map((hostname) => ({
              label: `Also on ${hostname}`,
              value: customDomainUrl(endpoint, hostname),
            })),
          ]
        : [],
    [customDomains, endpoint, platformUrl, primaryHostname]
  );
  // The snippets carry the SAME address the bar above shows: a snippet is what a
  // developer pastes, so it must not disagree with the URL on screen.
  const snippets = useMemo(() => buildSnippets(endpoint, { url }), [endpoint, url]);
  const responseExample = useMemo(() => buildResponseExample(endpoint), [endpoint]);

  const mintKey = useMutation({
    mutationFn: () => apiKeysApi.mint().then(res => res.data.data),
    onSuccess: key => {
      setMintedKey(key);
      toast.success('API key minted', 'Copy it now — it is shown only once.');
    },
    onError: () => toast.error('Failed to mint API key'),
  });

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleCopyUrl = async () => {
    await handleCopy(url, 'URL');
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 1500);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          Call this AI Service
        </h2>
      </div>

      <div className="p-4">
        {/* URL bar */}
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-3 py-2.5">
          <span className="rounded-md bg-indigo-100 dark:bg-indigo-900 px-2 py-0.5 font-mono text-xs font-bold text-indigo-700 dark:text-indigo-300">
            {endpoint.method}
          </span>
          <span
            className="flex-1 truncate font-mono text-sm text-gray-900 dark:text-gray-100"
            data-testid="service-url"
          >
            {url}
          </span>
          {primaryHostname && (
            <span
              data-testid="service-url-custom-domain-badge"
              className="shrink-0 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
            >
              On your custom domain
            </span>
          )}
          <button
            type="button"
            onClick={handleCopyUrl}
            className="inline-flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 shrink-0"
          >
            {urlCopied ? (
              <CheckIcon className="mr-1 h-4 w-4 text-green-500" />
            ) : (
              <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
            )}
            Copy
          </button>
        </div>

        {/* The other addresses that reach the same service (task 857). Nothing
            at all for an account with no custom domain — the platform URL is
            then the primary one above, and repeating it would be noise. */}
        {alternateUrls.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="service-url-alternates">
            {alternateUrls.map((alternate) => (
              <li
                key={alternate.value}
                data-testid="service-url-alternate"
                className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
              >
                <span className="font-medium">{alternate.label}</span>
                <span className="min-w-0 break-all font-mono">{alternate.value}</span>
                <button
                  type="button"
                  onClick={() => handleCopy(alternate.value, alternate.label)}
                  aria-label={`Copy ${alternate.label}`}
                  className="inline-flex shrink-0 items-center font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  <ClipboardDocumentIcon className="mr-1 h-3.5 w-3.5" />
                  Copy
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Auth note + mint */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          <span>
            Authenticate with an account API key in the{' '}
            <code className="rounded bg-gray-100 dark:bg-gray-700 px-1 py-0.5 font-mono text-xs text-gray-700 dark:text-gray-300">
              x-api-key
            </code>{' '}
            header.
          </span>
          <button
            type="button"
            onClick={() => mintKey.mutate()}
            disabled={mintKey.isPending}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <KeyIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            {mintKey.isPending ? 'Minting…' : 'Mint API key'}
          </button>
          {apiKeysPageEnabled && (
            <Link
              to="/settings/api-keys"
              data-testid="manage-api-keys-link"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Manage all API keys →
            </Link>
          )}
        </div>

        {/* One-time key reveal (no persistent key UI exists in the app — the
            plaintext is only ever available in this mint response). */}
        {mintedKey && (
          <div
            className="mt-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3"
            data-testid="minted-key-reveal"
          >
            <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
              Your new API key — copy it now, it will not be shown again.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 px-2 py-1.5 font-mono text-xs text-gray-900 dark:text-gray-100">
                {mintedKey.key}
              </code>
              <button
                type="button"
                onClick={() => handleCopy(mintedKey.key, 'API key')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 shrink-0"
              >
                Copy key
              </button>
            </div>
          </div>
        )}

        {/* Language tabs + snippet */}
        <div className="mt-5">
          <div className="inline-flex gap-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
            {LANGUAGES.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setLanguage(id)}
                className={`rounded-md px-3.5 py-1.5 text-sm font-medium ${
                  language === id
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <CodeBlock
            code={snippets[language]}
            copyLabel="Snippet"
            onCopy={handleCopy}
            testId="call-snippet"
          />

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Response
          </p>
          <CodeBlock
            code={responseExample}
            copyLabel="Response example"
            onCopy={handleCopy}
            testId="response-example"
          />
        </div>
      </div>
    </div>
  );
}
